import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import PredictionList, { PredictionListSkeleton } from '../components/PredictionList';
import RecommendBookies from '../components/RecommendBookies';
import SocialLinks from '../components/SocialLinks';
import apiService from '../utils/api';
import dataTracker from '../utils/dataTracker';
import { pageSEO } from '../utils/seo';

const Home = () => {
  const [predictions, setPredictions] = useState(null); // Paginated predictions from server
  const [allPredictionsCache, setAllPredictionsCache] = useState(null); // Cache for all predictions
  const [loading, setLoading] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false); // Separate loader for fetching all predictions
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortType, setSortType] = useState('time'); // Sorting state lifted up
  const [showAllLeagues, setShowAllLeagues] = useState(false); // State for toggling leagues
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Загрузка прогнозов
  useEffect(() => {
    const fetchAllPredictions = async () => {
      if (allPredictionsCache) return; // Use cache if available
      
      setLoadingAll(true);
      try {
        const all = {};
        // Fetch first page to get total pages
        const firstPageResponse = await apiService.predictions.getPredictions({ page: 1, limit: 10 });
        Object.assign(all, firstPageResponse.data.predictions);
        const total = firstPageResponse.data.pagination.pages;

        // Fetch remaining pages
        const promises = [];
        for (let i = 2; i <= total; i++) {
          promises.push(apiService.predictions.getPredictions({ page: i, limit: 10 }));
        }
        const responses = await Promise.all(promises);
        responses.forEach(res => Object.assign(all, res.data.predictions));
        
        setAllPredictionsCache(all);
      } catch (err) {
        console.error('Error fetching all predictions:', err);
        setError('Failed to load all predictions for filtering.');
      }
      setLoadingAll(false);
    };

    // ВСЕГДА загружаем полный кэш для корректной работы фильтров лиг
      fetchAllPredictions();
    
    if (sortType === 'time') {
      const fetchPaginatedPredictions = async () => {
        try {
          setLoading(true);
          const response = await apiService.predictions.getPredictions({ page, limit: 10 });
          setPredictions(response.data.predictions);
          setTotalPages(response.data.pagination.pages);
          setError(null);

          if (page === 1 && response.data.predictions) {
            const today = new Date().toISOString().split('T')[0];
            const todayMatches = response.data.predictions[today] || [];
            dataTracker.setTodayMatches(todayMatches);
          }
        } catch (err) {
          console.error('Error fetching predictions:', err);
          setError('Failed to load predictions. Please try again later.');
        } finally {
          setLoading(false);
        }
      };
      fetchPaginatedPredictions();
    }
  }, [page, sortType, allPredictionsCache]);

  // Handle sort change
  const handleSortChange = (newSortType) => {
    setPage(1); // Reset to first page on new sort
    setSortType(newSortType);
  };

  // Handle toggling all leagues visibility
  const handleToggleAllLeagues = () => {
    setShowAllLeagues(prev => !prev);
  };
  
  // Memoized hook for displaying predictions
  const displayedPredictions = useMemo(() => {
    if (sortType === 'time') {
      return predictions;
    }
    
    if (!allPredictionsCache) {
      return null;
    }

    // Flatten all predictions from cache
    const allFlat = Object.values(allPredictionsCache).flat();
    
    // Filter by league
    const filtered = allFlat.filter(p => p.league === sortType);
    
    // Paginate client-side
    const newTotalPages = Math.ceil(filtered.length / 10);
    setTotalPages(newTotalPages > 0 ? newTotalPages : 1);
    
    const start = (page - 1) * 10;
    const paginated = filtered.slice(start, start + 10);

    // Group by date for PredictionList component
    const grouped = {};
    paginated.forEach(p => {
      const dateKey = p.matchDate.split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(p);
    });
    
    return grouped;
  }, [sortType, page, predictions, allPredictionsCache]);

  
  // Функция для создания массива страниц для пагинации
  const getPageNumbers = () => {
    const maxPagesToShow = 5; // Максимальное количество страниц для отображения
    
    if (totalPages <= maxPagesToShow) {
      // Если всего страниц меньше или равно maxPagesToShow, показываем все
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // Вычисляем диапазон страниц для отображения
    let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    // Корректировка, если мы близко к концу
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    
    // Добавляем индикаторы для пропущенных страниц
    if (startPage > 1) {
      pages.unshift('ellipsis-start');
      pages.unshift(1);
    }
    
    if (endPage < totalPages) {
      pages.push('ellipsis-end');
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  return (
    <div className="container-narrow py-6">
      <Helmet>
        <title>{pageSEO.home.title}</title>
        <meta name="description" content={pageSEO.home.description} />
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-2xl md:text-3xl sm:text-2xl font-bold text-center mb-2">
          Football Predictions for Today
        </h1>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
          The Smart Way to Bet on Football
        </p>
      </motion.div>
      
      {/* Отображение ошибки, если есть */}
      {error && (
        <motion.div 
          className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 p-4 rounded-lg mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <p>{error}</p>
        </motion.div>
      )}
      
      {/* Отображение скелета загрузки или списка прогнозов */}
      {(loading || loadingAll) ? (
        <PredictionListSkeleton />
      ) : (
        <>
          <PredictionList 
            predictions={displayedPredictions} 
            sortType={sortType}
            onSortChange={handleSortChange}
            allPredictions={allPredictionsCache || {}}
            showAllLeagues={showAllLeagues}
            onToggleAllLeagues={handleToggleAllLeagues}
          />
          
          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-4 mb-6">
              {/* Кнопка "Предыдущая" */}
              <motion.button
                className={`px-3 py-1 rounded-md ${
                  page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-dark-light dark:text-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-light dark:text-gray-300 dark:hover:bg-dark'
                }`}
                onClick={() => page > 1 && setPage(page - 1)}
                whileHover={page > 1 ? { scale: 1.05 } : {}}
                whileTap={page > 1 ? { scale: 0.95 } : {}}
                disabled={page === 1}
              >
                Prev
              </motion.button>
              
              {/* Номера страниц */}
              {getPageNumbers().map((pageNum, index) => {
                // Отображение многоточия для пропущенных страниц
                if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
                  return (
                    <div
                      key={`${pageNum}-${index}`}
                      className="w-10 h-10 flex items-center justify-center text-gray-500 dark:text-gray-400"
                    >
                      ...
                    </div>
                  );
                }
                
                // Отображение обычной кнопки страницы
                return (
                  <motion.button
                    key={pageNum}
                    className={`w-10 h-10 rounded-full ${
                      pageNum === page
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 dark:bg-dark-light text-gray-700 dark:text-gray-300'
                    }`}
                    onClick={() => setPage(pageNum)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {pageNum}
                  </motion.button>
                );
              })}
              
              {/* Кнопка "Следующая" */}
              <motion.button
                className={`px-3 py-1 rounded-md ${
                  page === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-dark-light dark:text-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-light dark:text-gray-300 dark:hover:bg-dark'
                }`}
                onClick={() => page < totalPages && setPage(page + 1)}
                whileHover={page < totalPages ? { scale: 1.05 } : {}}
                whileTap={page < totalPages ? { scale: 0.95 } : {}}
                disabled={page === totalPages}
              >
                Next
              </motion.button>
            </div>
          )}
        </>
      )}
  
      {/* Раздел рекомендуемых букмекеров */}
      <RecommendBookies />

      {/* Социальные сети */}
      <SocialLinks />

      {/* Информационный раздел в конце страницы */}
      <motion.div
        className="mt-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h2 className="text-xl font-bold mb-3">Professional Football Predictions & Expert Analysis</h2>
        <div className="text-gray-700 dark:text-gray-300 mb-4 relative">
          <div className={`transition-all duration-300 ${isExpanded ? 'max-h-none' : 'max-h-20 overflow-hidden'}`}>
            <p>
              Welcome to Main-Bet, your premier destination for <strong>expert football predictions</strong> and comprehensive match analysis. Our team of professional analysts combines years of betting experience with advanced statistical modeling to deliver the most accurate <strong>football betting tips</strong> available today. We meticulously analyze every aspect of the game - from team formations and tactical approaches to player fitness and historical performance data.
            </p>
            
            <p className="mt-4">
              Our <strong>daily football predictions</strong> cover all major leagues including Premier League, La Liga, Serie A, Bundesliga, and Champions League. Each prediction is backed by thorough research, examining crucial factors such as recent form, head-to-head statistics, home/away performance, and team news. Whether you're looking for match result predictions, over/under goals tips, or both teams to score forecasts, Main-Bet provides comprehensive coverage to enhance your betting strategy and maximize your winning potential.
            </p>
          </div>
          
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-primary font-medium hover:underline focus:outline-none relative z-10"
          >
            {isExpanded ? 'Show less' : 'Read more...'}
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Please bet responsibly. All predictions are for informational purposes only. 18+ only.
        </p>
      </motion.div>
    </div>
  );
};

export default Home; 